import { useState, useCallback, useRef, useEffect } from "react";
import { useAtelierStore, JOB_STATUSES, getJobStatusFromOrdre } from "@store/atelierStore";
import { getOrdreById } from "@store/ordreStore";
import { WorkCharge } from "../../types/garage/atelier";
import { snapToGrid } from "@utils/timeGrid";
import { FloatingGhost } from "./FloatingGhostOverlay";
import { getMonday, toISO, AvailabilityBlock } from "@store/disponibiliteStore";
import { showNotification } from "context/notification.service";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import {
  GridPreview,
  HOURS_START,
  HOURS_END,
  SNAP_PX,
  SNAP_HOUR,
  HOUR_BLOCK_HEIGHT,
} from "./gridTypes";

/** Check if placing a job would overlap with existing jobs for the same worker */
function hasOverlap(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  excludeJobId?: string,
): boolean {
  const jobs = useAtelierStore.getState().jobs;
  const end = startHour + durationHours;
  return jobs.some((j) => {
    if (j.id === excludeJobId) return false;
    if (j.workerId !== workerId || j.date !== date) return false;
    const jEnd = j.startHour + j.durationHours;
    return startHour < jEnd && end > j.startHour;
  });
}

/** Find a same-ordre job that overlaps OR is adjacent to the given position (for merge) */
function findMergeableSameOrdreJob(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  ordreId: string,
  excludeJobId: string,
) {
  const jobs = useAtelierStore.getState().jobs;
  const end = startHour + durationHours;
  const tolerance = SNAP_HOUR;
  return jobs.find((j) => {
    if (j.id === excludeJobId) return false;
    if (j.workerId !== workerId || j.date !== date) return false;
    if (j.ordre_id !== ordreId) return false;
    const jEnd = j.startHour + j.durationHours;
    // Overlapping OR adjacent (within snap tolerance)
    return (startHour < jEnd + tolerance) && (end > j.startHour - tolerance);
  }) ?? null;
}

/** Check if overlap exists ONLY with non-same-ordre jobs (true overlap, not a merge candidate) */
function hasNonMergeableOverlap(
  workerId: string,
  date: string,
  startHour: number,
  durationHours: number,
  excludeJobId: string,
  ordreId?: string,
): boolean {
  const jobs = useAtelierStore.getState().jobs;
  const end = startHour + durationHours;
  return jobs.some((j) => {
    if (j.id === excludeJobId) return false;
    if (j.workerId !== workerId || j.date !== date) return false;
    const jEnd = j.startHour + j.durationHours;
    if (!(startHour < jEnd && end > j.startHour)) return false;
    // Same-ordre overlap is a merge candidate, not a real conflict
    if (ordreId && j.ordre_id === ordreId) return false;
    return true;
  });
}

function getStatusConfig(status: string) {
  return JOB_STATUSES.find((s) => s.id === status)!;
}

/** Find the first pause/congé zone that overlaps the given time range for a worker */
function findOverlappingPauseZone(
  workerId: string,
  date: string,
  startHour: number,
  endHour: number,
): { start: number; end: number } | null {
  const blocks = useDispoAtelierStore.getState().blocks;
  const d = new Date(date + "T00:00:00");
  const jsDay = d.getDay();
  const dow = jsDay === 0 ? 6 : jsDay - 1;
  for (const b of blocks) {
    if (b.workerId !== workerId) continue;
    if (b.zoneType !== "pause" && b.zoneType !== "conge") continue;
    const matches = b.date === date || (b.recurring && b.dayOfWeek === dow);
    if (!matches) continue;
    const bEnd = b.startHour + b.durationHours;
    // Overlaps but is not fully contained within the pause
    if (startHour < bEnd && endHour > b.startHour
      && !(startHour >= b.startHour && endHour <= bEnd)) {
      return { start: b.startHour, end: bEnd };
    }
  }
  return null;
}

export interface ReportPausePending {
  chargeId: string;
  targetStatus: "reportee" | "en_pause";
  clientName: string;
}

export interface LunchOverlapPending {
  type: "job" | "charge";
  jobId?: string;
  chargeId?: string;
  workerId: string;
  startHour: number;
  durationHours: number;
  date: string;
  lunchStart: number;
  lunchEnd: number;
}

export interface JobMergePending {
  draggedJobId: string;
  targetJobId: string;
  mergedStartHour: number;
  mergedDurationHours: number;
  draggedClientName: string;
  targetClientName: string;
}

export function useAtelierDrag(
  gridScrollRef: React.RefObject<HTMLDivElement | null>,
  sidebarRef: React.RefObject<HTMLDivElement | null>,
) {
  const updateJob = useAtelierStore((s) => s.updateJob);
  const placeChargeAsJob = useAtelierStore((s) => s.placeChargeAsJob);
  const returnJobToCharge = useAtelierStore((s) => s.returnJobToCharge);
  const moveChargeToStatus = useAtelierStore((s) => s.moveChargeToStatus);
  const mergeCharges = useAtelierStore((s) => s.mergeCharges);
  const getFilteredJobs = useAtelierStore((s) => s.getFilteredJobs);

  const [lunchOverlapPending, setLunchOverlapPending] = useState<LunchOverlapPending | null>(null);
  const [reportPausePending, setReportPausePending] = useState<ReportPausePending | null>(null);
  const [jobMergePending, setJobMergePending] = useState<JobMergePending | null>(null);

  // Charge drag from sidebar
  const chargeDragRef = useRef<{
    chargeId: string;
    hours: number;
    clientName: string;
    ordreId?: string;
  } | null>(null);

  // Job drag from grid
  const jobDragRef = useRef<{
    jobId: string;
    mode: "move" | "resize";
    startY: number;
    origStartHour: number;
    origDuration: number;
    origWorkerId: string;
  } | null>(null);

  const [gridPreview, _setGridPreview] = useState<GridPreview | null>(null);
  const gridPreviewRef = useRef<GridPreview | null>(null);
  const setGridPreview = useCallback((v: GridPreview | null) => {
    gridPreviewRef.current = v;
    _setGridPreview(v);
  }, []);
  const [floatingGhost, setFloatingGhost] = useState<FloatingGhost | null>(
    null,
  );
  const [overDropZone, _setOverDropZone] = useState(false);
  const overDropZoneRef = useRef(false);
  const setOverDropZone = useCallback((v: boolean) => {
    overDropZoneRef.current = v;
    _setOverDropZone(v);
  }, []);
  const [hoveredChargeSection, _setHoveredChargeSection] = useState<
    WorkCharge["status"] | null
  >(null);
  const hoveredChargeSectionRef = useRef<WorkCharge["status"] | null>(null);
  const setHoveredChargeSection = useCallback((v: WorkCharge["status"] | null) => {
    hoveredChargeSectionRef.current = v;
    _setHoveredChargeSection(v);
  }, []);

  // Charge-on-charge merge target
  const [mergeTargetChargeId, _setMergeTargetChargeId] = useState<string | null>(null);
  const mergeTargetChargeIdRef = useRef<string | null>(null);
  const setMergeTargetChargeId = useCallback((v: string | null) => {
    mergeTargetChargeIdRef.current = v;
    _setMergeTargetChargeId(v);
  }, []);

  const detectWorkerCol = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-worker-col]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-worker-col");
      });
      return found;
    },
    [gridScrollRef],
  );

  const detectDayDate = useCallback(
    (clientX: number): string | null => {
      if (!gridScrollRef.current) return null;
      const cols =
        gridScrollRef.current.querySelectorAll<HTMLElement>("[data-day-date]");
      let found: string | null = null;
      cols.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX < r.right)
          found = el.getAttribute("data-day-date");
      });
      return found;
    },
    [gridScrollRef],
  );

  const isOverGrid = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!gridScrollRef.current) return false;
      const r = gridScrollRef.current.getBoundingClientRect();
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      );
    },
    [gridScrollRef],
  );

  const isOverSidebar = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!sidebarRef.current) return false;
      const r = sidebarRef.current.getBoundingClientRect();
      return (
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom
      );
    },
    [sidebarRef],
  );

  const detectChargeSection = useCallback(
    (clientX: number, clientY: number): WorkCharge["status"] | null => {
      if (!sidebarRef.current) return null;
      const sections = sidebarRef.current.querySelectorAll<HTMLElement>(
        "[data-charge-section]",
      );
      let found: WorkCharge["status"] | null = null;
      sections.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (
          clientX >= r.left &&
          clientX <= r.right &&
          clientY >= r.top &&
          clientY <= r.bottom
        ) {
          found = el.getAttribute(
            "data-charge-section",
          ) as WorkCharge["status"];
        }
      });
      return found;
    },
    [sidebarRef],
  );

  /** Detect which charge card the pointer is over, returning its id + ordre_id */
  const detectChargeCard = useCallback(
    (clientX: number, clientY: number): { id: string; ordreId: string } | null => {
      if (!sidebarRef.current) return null;
      const cards = sidebarRef.current.querySelectorAll<HTMLElement>("[data-charge-id]");
      let found: { id: string; ordreId: string } | null = null;
      cards.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
          found = {
            id: el.getAttribute("data-charge-id")!,
            ordreId: el.getAttribute("data-charge-ordre") ?? "",
          };
        }
      });
      return found;
    },
    [sidebarRef],
  );

  const handleGlobalPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // --- Job drag ---
      const jd = jobDragRef.current;
      if (jd) {
        if (jd.mode === "resize") {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / SNAP_PX);
          const newDur = snapToGrid(
            jd.origDuration + snapDelta * SNAP_HOUR,
            SNAP_HOUR,
          );
          const clamped = Math.max(
            SNAP_HOUR,
            Math.min(newDur, HOURS_END - jd.origStartHour),
          );
          const dayDate = detectDayDate(e.clientX);
          const resizeDate = dayDate || useAtelierStore.getState().selectedDate;
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: jd.origStartHour,
            durationHours: clamped,
            workerId: jd.origWorkerId,
            date: dayDate ?? undefined,
            overlap: hasOverlap(jd.origWorkerId, resizeDate, jd.origStartHour, clamped, jd.jobId),
          });
          setFloatingGhost(null);
          setOverDropZone(false);
          return;
        }

        const overGrid = isOverGrid(e.clientX, e.clientY);
        const overSide = isOverSidebar(e.clientX, e.clientY);

        if (overGrid) {
          const deltaY = e.clientY - jd.startY;
          const snapDelta = Math.round(deltaY / SNAP_PX);
          const newStart = snapToGrid(
            jd.origStartHour + snapDelta * SNAP_HOUR,
            SNAP_HOUR,
          );
          const clamped = Math.max(
            HOURS_START,
            Math.min(newStart, HOURS_END - jd.origDuration),
          );
          const newWorkerId = detectWorkerCol(e.clientX) ?? jd.origWorkerId;
          const dayDate = detectDayDate(e.clientX);
          const moveDate = dayDate || useAtelierStore.getState().selectedDate;
          const draggedJob = useAtelierStore.getState().jobs.find((j) => j.id === jd.jobId);
          const dragOrdreId = draggedJob?.ordre_id;
          // Only flag as overlap if conflict is with a different ordre (same-ordre = merge candidate)
          const realOverlap = dragOrdreId
            ? hasNonMergeableOverlap(newWorkerId, moveDate, clamped, jd.origDuration, jd.jobId, dragOrdreId)
            : hasOverlap(newWorkerId, moveDate, clamped, jd.origDuration, jd.jobId);
          const mergeCandidateJob = dragOrdreId
            ? findMergeableSameOrdreJob(newWorkerId, moveDate, clamped, jd.origDuration, dragOrdreId, jd.jobId)
            : null;
          if (mergeCandidateJob) console.log("[merge-preview]", mergeCandidateJob.id, mergeCandidateJob.clientName);
          setGridPreview({
            type: "job",
            jobId: jd.jobId,
            startHour: clamped,
            durationHours: jd.origDuration,
            workerId: newWorkerId,
            date: dayDate ?? undefined,
            overlap: realOverlap,
            mergeTarget: !!mergeCandidateJob,
            mergeTargetJobId: mergeCandidateJob?.id,
          });
          setFloatingGhost(null);
          setOverDropZone(false);
        } else {
          const origJob = getFilteredJobs().find((j) => j.id === jd.jobId);
          const config = origJob
            ? (() => {
                if (origJob.ordre_id) {
                  const ordre = getOrdreById(origJob.ordre_id);
                  if (ordre) return getStatusConfig(getJobStatusFromOrdre(ordre.status));
                }
                return getStatusConfig(origJob.status);
              })()
            : JOB_STATUSES[0];
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: origJob?.clientName ?? "",
            hours: jd.origDuration,
            color: config.color,
            bgColor: config.bgColor,
          });
          setOverDropZone(overSide);
        }
        return;
      }

      // --- Charge drag ---
      const cd = chargeDragRef.current;
      if (cd) {
        const overGrid = isOverGrid(e.clientX, e.clientY);

        if (overGrid) {
          const workerId = detectWorkerCol(e.clientX);
          const dayDate = detectDayDate(e.clientX);
          if (workerId && gridScrollRef.current) {
            const gridRect = gridScrollRef.current.getBoundingClientRect();
            const relY =
              e.clientY - gridRect.top + gridScrollRef.current.scrollTop;
            const rawHour = HOURS_START + relY / HOUR_BLOCK_HEIGHT;
            const startHour = snapToGrid(
              Math.max(HOURS_START, Math.min(rawHour, HOURS_END - cd.hours)),
              SNAP_HOUR,
            );
            const chargeDate = dayDate || useAtelierStore.getState().selectedDate;
            setGridPreview({
              type: "charge",
              chargeId: cd.chargeId,
              startHour,
              durationHours: cd.hours,
              workerId,
              clientName: cd.clientName,
              date: dayDate ?? undefined,
              overlap: hasOverlap(workerId, chargeDate, startHour, cd.hours),
            });
          } else {
            // Over the grid area but not on a worker column (e.g. hour labels)
            // Keep last preview if any, clear ghost so it doesn't cover the grid
            setGridPreview(null);
          }
          setFloatingGhost(null);
          setHoveredChargeSection(null);
        } else {
          setGridPreview(null);
          setFloatingGhost({
            x: e.clientX,
            y: e.clientY,
            width: 160,
            height: 40,
            clientName: cd.clientName,
            hours: cd.hours,
            color: "#6366F1",
            bgColor: "#E0E6FF",
          });
          setHoveredChargeSection(detectChargeSection(e.clientX, e.clientY));

          // Detect merge target: same ordre_id, different charge
          if (cd.ordreId) {
            const target = detectChargeCard(e.clientX, e.clientY);
            if (target && target.id !== cd.chargeId && target.ordreId === cd.ordreId) {
              setMergeTargetChargeId(target.id);
            } else {
              setMergeTargetChargeId(null);
            }
          } else {
            setMergeTargetChargeId(null);
          }
        }
        return;
      }
    },
    [
      detectWorkerCol,
      isOverGrid,
      isOverSidebar,
      getFilteredJobs,
      detectChargeSection,
      detectChargeCard,
      gridScrollRef,
    ],
  );

  const handleGlobalPointerUp = useCallback(() => {
    const gp = gridPreviewRef.current;
    const jd = jobDragRef.current;
    if (jd) {
      if (overDropZoneRef.current && jd.mode === "move") {
        returnJobToCharge(jd.jobId);
      } else if (gp && gp.type === "job") {
        const dropDate = gp.date || useAtelierStore.getState().selectedDate;
        const dropEnd = gp.startHour + gp.durationHours;

        // Check if dropped adjacent to (touching) a same-ordre job → offer merge
        const draggedJob = useAtelierStore.getState().jobs.find((j) => j.id === jd.jobId);
        if (draggedJob?.ordre_id && jd.mode === "move") {
          const adjacentTarget = useAtelierStore.getState().jobs.find((j) => {
            if (j.id === jd.jobId || j.workerId !== gp.workerId || j.date !== dropDate) return false;
            if (j.ordre_id !== draggedJob.ordre_id) return false;
            const jEnd = j.startHour + j.durationHours;
            // Adjacent: dragged top touches target bottom, or dragged bottom touches target top
            // Also allow slight overlap (within SNAP_HOUR tolerance)
            const tolerance = SNAP_HOUR;
            return (Math.abs(gp.startHour - jEnd) < tolerance) || (Math.abs(dropEnd - j.startHour) < tolerance);
          });
          if (adjacentTarget) {
            const tEnd = adjacentTarget.startHour + adjacentTarget.durationHours;
            const mergedStart = Math.min(gp.startHour, adjacentTarget.startHour);
            const mergedEnd = Math.max(dropEnd, tEnd);
            setJobMergePending({
              draggedJobId: jd.jobId,
              targetJobId: adjacentTarget.id,
              mergedStartHour: mergedStart,
              mergedDurationHours: +(mergedEnd - mergedStart).toFixed(4),
              draggedClientName: draggedJob.clientName,
              targetClientName: adjacentTarget.clientName,
            });
            jobDragRef.current = null;
            setGridPreview(null);
            setFloatingGhost(null);
            setOverDropZone(false);
            return;
          }
        }

        // Check if overlapping a same-ordre job → merge
        if (draggedJob?.ordre_id) {
          const overlapMergeTarget = findMergeableSameOrdreJob(gp.workerId, dropDate, gp.startHour, gp.durationHours, draggedJob.ordre_id, jd.jobId);
          if (overlapMergeTarget) {
            const mergedStart = overlapMergeTarget.startHour;
            const mergedDuration = +(draggedJob.durationHours + overlapMergeTarget.durationHours).toFixed(4);
            setJobMergePending({
              draggedJobId: jd.jobId,
              targetJobId: overlapMergeTarget.id,
              mergedStartHour: mergedStart,
              mergedDurationHours: mergedDuration,
              draggedClientName: draggedJob.clientName,
              targetClientName: overlapMergeTarget.clientName,
            });
            jobDragRef.current = null;
            setGridPreview(null);
            setFloatingGhost(null);
            setOverDropZone(false);
            return;
          }
        }

        if (hasOverlap(gp.workerId, dropDate, gp.startHour, gp.durationHours, jd.jobId)) {
          showNotification({ type: "error", message: "Impossible : ce créneau chevauche un autre travail" });
        } else {
          // Check pause zone overlap
          const pauseZone = findOverlappingPauseZone(gp.workerId, dropDate, gp.startHour, dropEnd);
          if (pauseZone && jd.mode === "move") {
            setLunchOverlapPending({
              type: "job",
              jobId: jd.jobId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
              lunchStart: pauseZone.start,
              lunchEnd: pauseZone.end,
            });
          } else {
            const updates: Parameters<typeof updateJob>[1] = {
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              workerId: gp.workerId,
            };
            if (gp.date) updates.date = gp.date;
            updateJob(jd.jobId, updates);
          }
        }
      }
      jobDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setOverDropZone(false);
      return;
    }

    const cd = chargeDragRef.current;
    if (cd) {
      // Charge-on-charge merge (same ordre)
      if (mergeTargetChargeIdRef.current) {
        mergeCharges(mergeTargetChargeIdRef.current, cd.chargeId);
        chargeDragRef.current = null;
        setGridPreview(null);
        setFloatingGhost(null);
        setHoveredChargeSection(null);
        setMergeTargetChargeId(null);
        return;
      }

      if (gp && gp.type === "charge") {
        const dropDate = gp.date || useAtelierStore.getState().selectedDate;
        if (hasOverlap(gp.workerId, dropDate, gp.startHour, gp.durationHours)) {
          showNotification({ type: "error", message: "Impossible : ce créneau chevauche un autre travail" });
        } else {
          // Check pause zone overlap
          const jobEnd = gp.startHour + gp.durationHours;
          const pauseZone = findOverlappingPauseZone(gp.workerId, dropDate, gp.startHour, jobEnd);
          if (pauseZone) {
            setLunchOverlapPending({
              type: "charge",
              chargeId: cd.chargeId,
              workerId: gp.workerId,
              startHour: gp.startHour,
              durationHours: gp.durationHours,
              date: dropDate,
              lunchStart: pauseZone.start,
              lunchEnd: pauseZone.end,
            });
          } else {
            placeChargeAsJob(
              cd.chargeId,
              gp.workerId,
              gp.startHour,
              gp.durationHours,
              gp.date,
            );
          }
        }
      } else if (hoveredChargeSectionRef.current) {
        const section = hoveredChargeSectionRef.current;
        if (section === "reportee" || section === "en_pause") {
          setReportPausePending({
            chargeId: cd.chargeId,
            targetStatus: section,
            clientName: cd.clientName,
          });
        } else {
          moveChargeToStatus(cd.chargeId, section);
        }
      }
      chargeDragRef.current = null;
      setGridPreview(null);
      setFloatingGhost(null);
      setHoveredChargeSection(null);
      setMergeTargetChargeId(null);
      return;
    }
  }, [
    updateJob,
    placeChargeAsJob,
    returnJobToCharge,
    moveChargeToStatus,
    mergeCharges,
  ]);

  const handleChargeDragStart = useCallback(
    (e: React.PointerEvent, charge: WorkCharge) => {
      // Pointer capture is now handled by ChargeCard itself (on the card div)
      chargeDragRef.current = {
        chargeId: charge.id,
        hours: charge.hours,
        clientName: charge.clientName,
        ordreId: charge.ordre_id,
      };
      setFloatingGhost({
        x: e.clientX,
        y: e.clientY,
        width: 160,
        height: Math.max((charge.hours / SNAP_HOUR) * SNAP_PX, 40),
        clientName: charge.clientName,
        hours: charge.hours,
        color: "#6366F1",
        bgColor: "#E0E6FF",
      });

      // Auto-navigate to the charge's car window date if not currently visible
      const depositDate = charge.carWindow?.depositDate;
      if (!depositDate) return;
      const restDate = charge.carWindow?.restitutionDate || depositDate;
      const { selectedDate, viewMode, setSelectedDate } = useAtelierStore.getState();
      if (viewMode === "jour") {
        // Only teleport if current day is outside the reception–restitution window
        if (selectedDate < depositDate || selectedDate > restDate) {
          setSelectedDate(depositDate);
        }
      } else {
        // Week view: only teleport if current week has NO overlap with the window
        const monday = getMonday(selectedDate);
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);
        const weekOverlapsWindow = depositDate <= toISO(friday) && restDate >= toISO(monday);
        if (!weekOverlapsWindow) {
          setSelectedDate(depositDate);
        }
      }
    },
    [],
  );

  // Arrow keys navigate weeks/days — always active, and shifts preview date when dragging
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const { selectedDate, viewMode, setSelectedDate } = useAtelierStore.getState();
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -(viewMode === "semaine" ? 7 : 1) : (viewMode === "semaine" ? 7 : 1);
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + delta);
      setSelectedDate(toISO(d));

      // If dragging, shift the preview date so the ghost stays in place
      const gp = gridPreviewRef.current;
      if (gp?.date) {
        const pd = new Date(gp.date);
        pd.setDate(pd.getDate() + delta);
        setGridPreview({ ...gp, date: toISO(pd) });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setGridPreview]);

  const isDraggingAnything =
    !!(chargeDragRef.current || jobDragRef.current) ||
    !!(floatingGhost || gridPreview);
  const isDraggingJob =
    !!jobDragRef.current && jobDragRef.current.mode === "move";

  return {
    jobDragRef,
    gridPreview,
    setGridPreview,
    floatingGhost,
    overDropZone,
    hoveredChargeSection,
    mergeTargetChargeId,
    isDraggingAnything,
    isDraggingJob,
    handleGlobalPointerMove,
    handleGlobalPointerUp,
    handleChargeDragStart,
    lunchOverlapPending,
    setLunchOverlapPending,
    reportPausePending,
    setReportPausePending,
    jobMergePending,
    setJobMergePending,
  };
}
